/**
 * Single definition of "a valid Completeness threshold": a finite number in [0, 1].
 *
 * The form stores threshold as a string (the percent input needs an empty-string state) but the API
 * contract is a number, so several call sites must agree on what counts as valid. They differ only
 * in _policy_ on failure (drop / raise a validation error / commit raw for the validator to catch)
 *
 * - This owns the shared _predicate_ so those policies can't drift apart.
 *
 * Uses Number (not parseFloat) so trailing garbage like "0.9x" is invalid rather than truncated.
 */
type ParsedThreshold = { ok: true; value: number } | { ok: false; reason: "nan" | "range" }

/**
 * Parse a stored decimal threshold string/number into a validated 0-1 ratio. On failure, `reason`
 * distinguishes non-numeric ("nan") from out-of-range ("range") so callers can pick a message.
 */
export function parseThreshold(raw: string | number | null | undefined): ParsedThreshold {
  const value = Number(raw)
  if (raw == null || raw === "" || !Number.isFinite(value)) return { ok: false, reason: "nan" }
  if (value < 0 || value > 1) return { ok: false, reason: "range" }
  return { ok: true, value }
}

/**
 * True when `raw` parses to a finite number (range-independent; used by the percent input).
 */
export function isFiniteNumeric(raw: string | number | null | undefined): boolean {
  if (raw == null || raw === "") return false
  return Number.isFinite(Number(raw))
}
